"use client";

import { useState, useCallback, useEffect } from "react";
import type { ModeInfo, ProviderInfo } from "@/app/lib/types";
import { fetchProviders } from "@/app/lib/api";
import { getClientConfig } from "@/app/lib/clientConfig";

interface UseProvidersReturn {
  providers: ProviderInfo[];
  modes: ModeInfo[];
  selectedProviders: string[];
  isLoading: boolean;
  toggleProvider: (id: string) => void;
  setSelectedProviders: (ids: string[]) => void;
  loadProviders: () => Promise<void>;
}

export function useProviders(): UseProvidersReturn {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [modes, setModes] = useState<ModeInfo[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadProviders = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await fetchProviders();
      // Honor the server-driven kill-switch (v1.6 client-config): an operator
      // can hide a provider during an outage without a redeploy. Default
      // providers_disabled is [] → no effect.
      const disabled = getClientConfig().providers_disabled || [];
      const enabled = resp.providers.filter(
        (p) => p.enabled && !disabled.includes(p.id)
      );
      setProviders(enabled);
      setModes(resp.modes || []);
      // Initialize selected providers to all enabled if not yet set
      setSelectedProviders((prev) =>
        prev.length === 0 ? enabled.map((p) => p.id) : prev
      );
    } catch (err) {
      console.error("Failed to load providers:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const toggleProvider = useCallback((id: string) => {
    setSelectedProviders((prev) => {
      if (prev.includes(id)) {
        // Don't allow deselecting all
        if (prev.length <= 1) return prev;
        return prev.filter((p) => p !== id);
      }
      return [...prev, id];
    });
  }, []);

  return {
    providers,
    modes,
    selectedProviders,
    isLoading,
    toggleProvider,
    setSelectedProviders,
    loadProviders,
  };
}
